import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { ADD_ZONE, D102_1, INCOMPLETE, ZONE_SELECTION_INDEX } from 'rpqs/constants/RpqsConstant'
import EntityIndicatorDialog from '../EntityIndicatorDialog'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import ZoneSelection from './zones/ZoneSelection'
import ZonePanel from './zones/ZonePanel'
import CollectivityAction from 'rpqs/actions/CollectivityAction'
import { Button, Grid, Typography } from '@mui/material'
import i18n from 'simple-react-i18n'
import SelectChipIndicator from '../../SelectChipIndicator'
import Input from 'components/forms/Input'
import NumberField from 'components/forms/NumberField'
import { maxBy } from 'lodash'

const DeleteZonePanel = ({
    setPanelTitle = () => {},
    zoneToRemove,
}) => {
    const {
        zones,
    } = useSelector(store => ({
        zones: store.CollectivityReducer.zones,
    }), shallowEqual)

    const dispatch = useDispatch()

    const handleRemoveZone = () => {
        const newZones = zones.filter((z) => z.id !== zoneToRemove.id)
        dispatch(CollectivityAction.updateZones(newZones))
        setPanelTitle(undefined)
    }

    return (
        <Grid size={'grow'} container direction={'column'} justifyContent={'space-between'} pr={1}>
            <Typography>{`${i18n.deleteAreaConfirmation} : `}<b>{zoneToRemove?.name}</b></Typography>
            <Grid container height={'fit-content'} justifyContent={'end'} gap={2} size={12}>
                <Button
                    variant='outlined'
                    onClick={() => setPanelTitle(undefined)}
                >
                    {i18n.cancel}
                </Button>
                <Button
                    variant='contained'
                    onClick={handleRemoveZone}
                >
                    {i18n.delete}
                </Button>
            </Grid>
        </Grid>
    )
}


DeleteZonePanel.propTypes = {
    setPanelTitle: PropTypes.func,
    zoneToRemove: PropTypes.shape({
        name: PropTypes.string,
        id: PropTypes.number,
    }),
}

const AddZonePanel = ({
    setPanelTitle = () => {},
}) => {
    const {
        zones,
        rpqs,
    } = useSelector(store => ({
        zones: store.CollectivityReducer.zones,
        rpqs: store.RpqsReducer.rpqs,
    }), shallowEqual)

    const dispatch = useDispatch()

    const [name, setName] = useState('')
    const [nbHab, setNbHab] = useState(0)
    const [pricePreviousYear, setPricePreviousYear] = useState('')
    const [priceCurrentYear, setPriceCurrentYear] = useState('')
    const [status, setStatus] = useState(INCOMPLETE)

    const year = rpqs.year

    const handleAddZone = () => {
        const zoneToAdd = { id: maxBy(zones, 'id').id+1, name, nbHab, status, [year-1]: { servicePrice: pricePreviousYear }, [year]: { servicePrice: priceCurrentYear } }
        dispatch(CollectivityAction.updateZones([...zones, zoneToAdd]))
        setPanelTitle(undefined)
    }

    return (
        <Grid size={'grow'} container direction={'column'} justifyContent={'space-between'} pr={1}>
            <Grid container gap={1} direction={'column'}>
                <Input
                    title={i18n.name}
                    value={name}
                    onChange={setName}
                />
                <Grid container gap={2} alignItems={'center'} pb={1}>
                    <Typography pl={2} fontWeight={'bold'}>{i18n.status}</Typography>
                    <SelectChipIndicator indicatorStatus={status} onChange={setStatus}/>
                </Grid>
                <NumberField
                    title={i18n.nbInhabitants}
                    value={nbHab}
                    onChange={setNbHab}
                />
                <NumberField
                    title={`${i18n.PpiceM3WaterIn} ${year-1}`}
                    value={pricePreviousYear}
                    floatValue
                    onChange={setPricePreviousYear}
                />
                <NumberField
                    title={`${i18n.PpiceM3WaterIn} ${year}`}
                    value={priceCurrentYear}
                    floatValue
                    onChange={setPriceCurrentYear}
                />
            </Grid>
            <Grid container justifyContent={'end'} gap={2}>
                <Button
                    variant='outlined'
                    onClick={() => setPanelTitle(undefined)}
                >
                    {i18n.cancel}
                </Button>
                <Button
                    variant='contained'
                    onClick={handleAddZone}
                    disabled={!(name && nbHab && pricePreviousYear && priceCurrentYear && status)}
                >
                    {i18n.add}
                </Button>
            </Grid>
        </Grid>
    )
}

AddZonePanel.propTypes = {
    setPanelTitle: PropTypes.func,
}

const DialogD102_1 = ({
    open = false,
    setOpen = () => {},
    title = '',
}) => {
    const {
        managementEntity,
        zones,
        zone,
    } = useSelector(store => ({
        managementEntity: store.CollectivityReducer.managementEntity,
        zone: store.CollectivityReducer.zone,
        zones: store.CollectivityReducer.zones,
    }), shallowEqual)

    const dispatch = useDispatch()
    const [entityDialog, setEntity] = useState(managementEntity)
    const [readMode, setReadMode] = useState(true)
    const [currentZoneIndex, setCurrentZoneIndex] = useState(ZONE_SELECTION_INDEX)
    const [panelTitle, setPanelTitle] = useState()
    const [zoneToRemove, setZoneToRemove] = useState()

    useEffect(() => {
        setEntity(managementEntity)
    }, [managementEntity])

    const handleRegister = () => {
        const newZones = zones.map((z) => {
            if (z.id !== zone.id) return z
            return zone
        })
        dispatch(CollectivityAction.updateZones(newZones))
    }

    const panelContent = () => {
        if (panelTitle === ADD_ZONE) return <AddZonePanel setPanelTitle={setPanelTitle}/>

        return zoneToRemove ? <DeleteZonePanel setPanelTitle={setPanelTitle} zoneToRemove={zoneToRemove}/> : undefined
    }

    return (
        <EntityIndicatorDialog
            open={open}
            setOpen={setOpen}
            title={title}
            hasNote={currentZoneIndex !== ZONE_SELECTION_INDEX || panelTitle}
            entityDialog={entityDialog}
            setEntityDialog={setEntity}
            readMode={readMode}
            setReadMode={currentZoneIndex === ZONE_SELECTION_INDEX ? null : setReadMode}
            codeIndicator={D102_1.code}
            onClose={() => setCurrentZoneIndex(ZONE_SELECTION_INDEX)}
            height={currentZoneIndex !== ZONE_SELECTION_INDEX && '100vh'}
            registerAction={handleRegister}
            panelProps={{
                title: i18n[panelTitle] || i18n.unknown,
                content: panelContent(),
                minWidth: currentZoneIndex === ZONE_SELECTION_INDEX ? 400 : 200,
            }}
        >
            {currentZoneIndex === ZONE_SELECTION_INDEX &&
                <ZoneSelection
                    setCurrentZone={setCurrentZoneIndex}
                    setPanelTitle={setPanelTitle}
                    setZoneToRemove={setZoneToRemove}
                />
            }
            {currentZoneIndex >= 0 &&
                <ZonePanel
                    zoneIndex={currentZoneIndex}
                    setCurrentZone={setCurrentZoneIndex}
                    readMode={readMode}
                />
            }
        </EntityIndicatorDialog>
    )
}

DialogD102_1.propTypes = {
    setOpen: PropTypes.func,
    open: PropTypes.bool,
    title: PropTypes.string,
}

export default DialogD102_1
